/**
 * Chat conversation store (Story 303).
 *
 * Light-weight reactive store that owns the visible message list and
 * cumulative token totals. The library's `Conversation` class adds value
 * for multi-turn API calls (rollback semantics, busy guards) but it depends
 * on an active `ClaudeProvider` connection — which is undesirable for unit
 * tests and unnecessary for the demo, where we drive the provider directly
 * from `<ChatView>`. The store therefore tracks history independently and
 * exposes `exportHistory()` so the view can pass the array straight to
 * `provider.streamMessages` / `provider.completeMessages`.
 *
 * Reset semantics match the spec on issue #15: clearing the visible
 * history is sufficient because there is no shared `Conversation` instance
 * to roll back.
 */
import { computed, reactive, ref } from 'vue';

export interface VisibleMessage {
  id: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  inputTokens?: number;
  outputTokens?: number;
}

let nextId = 0;
function makeId(role: string): string {
  nextId += 1;
  return `${role}-${nextId}`;
}

export function createChatStore(systemPrompt?: string) {
  const messages = reactive<VisibleMessage[]>([]);
  const streamingDraft = ref<string>('');
  const lastInputTokens = ref<number | null>(null);
  const lastOutputTokens = ref<number | null>(null);
  const totalInputTokens = ref<number>(0);
  const totalOutputTokens = ref<number>(0);

  if (systemPrompt) {
    messages.push({ id: makeId('system'), role: 'system', content: systemPrompt });
  }

  function appendUser(text: string): VisibleMessage {
    const msg: VisibleMessage = { id: makeId('user'), role: 'user', content: text };
    messages.push(msg);
    return msg;
  }

  function appendAssistant(
    text: string,
    usage?: { inputTokens: number; outputTokens: number },
  ): VisibleMessage {
    const msg: VisibleMessage = {
      id: makeId('assistant'),
      role: 'assistant',
      content: text,
      inputTokens: usage?.inputTokens,
      outputTokens: usage?.outputTokens,
    };
    messages.push(msg);
    if (usage) {
      lastInputTokens.value = usage.inputTokens;
      lastOutputTokens.value = usage.outputTokens;
      totalInputTokens.value += usage.inputTokens;
      totalOutputTokens.value += usage.outputTokens;
    }
    streamingDraft.value = '';
    return msg;
  }

  function reset(): void {
    messages.splice(0, messages.length);
    streamingDraft.value = '';
    lastInputTokens.value = null;
    lastOutputTokens.value = null;
    totalInputTokens.value = 0;
    totalOutputTokens.value = 0;
    if (systemPrompt) {
      messages.push({ id: makeId('system'), role: 'system', content: systemPrompt });
    }
  }

  function pushDelta(delta: string): void {
    streamingDraft.value += delta;
  }

  function exportHistory(): ReadonlyArray<{ role: 'user' | 'assistant'; content: string }> {
    return messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
  }

  return {
    messages,
    streamingDraft,
    lastInputTokens,
    lastOutputTokens,
    totalInputTokens,
    totalOutputTokens,
    cumulativeTokens: computed(() => totalInputTokens.value + totalOutputTokens.value),
    appendUser,
    appendAssistant,
    pushDelta,
    reset,
    exportHistory,
  };
}

export type ChatStore = ReturnType<typeof createChatStore>;
